<script setup>
// CHAT INTERNO (v78/v79, Kairo 04/07/2026): balao azul fixo (substitui o
// Copiloto), painel estilo iPhone com botao MINIMIZAR.
// - Online/Offline separados por papel; matriz de permissao no backend;
//   INATIVOS e BLOQUEADOS ficam fora das listas.
// - DM 1-a-1 com historico, EMOJIS (EmojiInput do Chatwoot) e ARQUIVOS
//   (pdf/png/jpg ate 15MB).
// - LOCALIZAR CLIENTE (CPF/telefone em qualquer formato) -> abre a conversa.
// - SO id 1: olho ao lado de cada nome = ESPIAO (ve o chat da pessoa,
//   somente leitura, botao Retornar volta pro proprio) e engrenagem ao lado
//   de ONLINE = bloquear/desbloquear o chat de usuarios.
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';
import InternalChatAPI from 'dashboard/api/internalChat';

const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.id === 1);

const isOpen = ref(false);
const view = ref('list'); // list | dm | locate | admin
const contactsList = ref([]);
const isChatBlocked = ref(false);
const activeContact = ref(null);
const dmMessages = ref([]);
const dmInput = ref('');
const isSending = ref(false);
const hasOlder = ref(false);
const dmBody = ref(null);
const showEmoji = ref(false);
const fileInput = ref(null);

// espiao (id1): quando setado, todas as listas/DMs sao DO usuario espiado
const spyUser = ref(null);

// engrenagem (id1)
const blockRows = ref([]);
const isLoadingBlocks = ref(false);

// localizar cliente
const locateMode = ref('cpf');
const locateQuery = ref('');
const locateResults = ref(null);
const isLocating = ref(false);

let contactsTimer = null;
let dmTimer = null;

const ROLE_LABELS = {
  administrator: 'Administrador',
  supervisor: 'Supervisor',
  operacional: 'Operacional',
  agent: 'Agente',
};
const ROLE_ORDER = ['administrator', 'supervisor', 'operacional', 'agent'];

// Bolinha do balao: conta USUARIOS com mensagem nao lida (nao mensagens)
const usersWithUnread = computed(() =>
  spyUser.value
    ? 0
    : contactsList.value.filter(c => (c.unread || 0) > 0).length
);

// Secoes expansiveis (clique no Online/Offline abre e fecha)
const showOnline = ref(false);
const showOffline = ref(false);

// Lista de conversas estilo WhatsApp: quem ja trocou mensagem, mais recente
// no topo — nunca some.
const conversations = computed(() =>
  contactsList.value
    .filter(c => c.last_message)
    .slice()
    .sort(
      (a, b) =>
        new Date(b.last_message.created_at) -
        new Date(a.last_message.created_at)
    )
);

// --- horarios SEMPRE em Brasilia -------------------------------------------
const TZ = 'America/Sao_Paulo';
const timeOnly = iso =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TZ,
  });
const dateKey = iso =>
  new Date(iso).toLocaleDateString('pt-BR', { timeZone: TZ });
const dayLabel = iso => {
  const key = dateKey(iso);
  const now = new Date();
  if (key === dateKey(now.toISOString())) return 'HOJE';
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  if (key === dateKey(yesterday.toISOString())) return 'ONTEM';
  return key;
};
const listTime = iso =>
  dateKey(iso) === dateKey(new Date().toISOString())
    ? timeOnly(iso)
    : new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        timeZone: TZ,
      });

// Mensagens da DM com separador de dia (HOJE/ONTEM/data), igual WhatsApp
const dmWithSeparators = computed(() => {
  const out = [];
  let lastKey = null;
  dmMessages.value.forEach(m => {
    const key = dateKey(m.created_at);
    if (key !== lastKey) {
      out.push({ separator: true, id: `sep-${key}`, label: dayLabel(m.created_at) });
      lastKey = key;
    }
    out.push(m);
  });
  return out;
});

const groupByRole = list => {
  const groups = [];
  ROLE_ORDER.forEach(role => {
    const users = list.filter(c => c.role === role);
    if (users.length) groups.push({ role, label: ROLE_LABELS[role], users });
  });
  return groups;
};

const onlineGroups = computed(() =>
  groupByRole(contactsList.value.filter(c => c.online))
);
const offlineGroups = computed(() =>
  groupByRole(contactsList.value.filter(c => !c.online))
);

const headerTitle = computed(() => {
  if (view.value === 'dm') return activeContact.value?.name;
  if (view.value === 'locate') return 'Localizar Cliente';
  if (view.value === 'admin') return 'Bloquear / Desbloquear';
  if (spyUser.value) return `Chat de ${spyUser.value.name}`;
  return 'Chat interno';
});

// v110: chave geral (Configurações > Chat Interno). Desligada => o widget
// SOME e os timers PARAM (zero polling — alívio real de carga).
const desativado = ref(false);

const desligarWidget = () => {
  desativado.value = true;
  clearInterval(contactsTimer);
  contactsTimer = null;
  clearInterval(dmTimer);
  dmTimer = null;
};

const fetchContacts = async () => {
  try {
    const { data } = await InternalChatAPI.contacts(spyUser.value?.id);
    if (data.desativado) return desligarWidget();
    contactsList.value = data.data || [];
    isChatBlocked.value = !!data.blocked;
  } catch (e) {
    // silencioso — proximo poll tenta de novo
  }
};

const scrollDmToBottom = () => {
  nextTick(() => {
    if (dmBody.value) dmBody.value.scrollTop = dmBody.value.scrollHeight;
  });
};

const fetchDm = async (initial = false) => {
  if (!activeContact.value) return;
  try {
    const { data } = await InternalChatAPI.messages(
      activeContact.value.id,
      null,
      spyUser.value?.id
    );
    if (data.desativado) return desligarWidget(); // v110: chave geral OFF
    const list = data.data || [];
    const lastId = dmMessages.value[dmMessages.value.length - 1]?.id;
    dmMessages.value = list;
    hasOlder.value = initial ? list.length >= 30 : hasOlder.value;
    if (initial || list[list.length - 1]?.id !== lastId) scrollDmToBottom();
    if (!spyUser.value) {
      InternalChatAPI.markRead(activeContact.value.id).catch(() => {});
      const contact = contactsList.value.find(
        c => c.id === activeContact.value.id
      );
      if (contact) contact.unread = 0;
    }
  } catch (e) {
    // silencioso
  }
};

const loadOlderDm = async () => {
  if (!dmMessages.value.length) return;
  try {
    const { data } = await InternalChatAPI.messages(
      activeContact.value.id,
      dmMessages.value[0].id,
      spyUser.value?.id
    );
    const older = data.data || [];
    dmMessages.value = [...older, ...dmMessages.value];
    hasOlder.value = older.length >= 30;
  } catch (e) {
    hasOlder.value = false;
  }
};

const openDm = contact => {
  activeContact.value = contact;
  dmMessages.value = [];
  hasOlder.value = false;
  showEmoji.value = false;
  view.value = 'dm';
  fetchDm(true);
  clearInterval(dmTimer);
  dmTimer = setInterval(fetchDm, 4000);
};

const closeDm = () => {
  clearInterval(dmTimer);
  dmTimer = null;
  activeContact.value = null;
  showEmoji.value = false;
  view.value = 'list';
  fetchContacts();
};

const sendDm = async (file = null) => {
  const content = dmInput.value.trim();
  if ((!content && !file) || isSending.value || spyUser.value) return;
  isSending.value = true;
  try {
    const { data } = await InternalChatAPI.send(
      activeContact.value.id,
      content,
      file
    );
    dmMessages.value = [...dmMessages.value, data];
    dmInput.value = '';
    showEmoji.value = false;
    scrollDmToBottom();
  } catch (e) {
    // mantem o texto no input pra reenviar
  } finally {
    isSending.value = false;
  }
};

const onFileSelected = event => {
  const [file] = event.target.files || [];
  if (file) sendDm(file);
  event.target.value = '';
};

const addEmoji = emoji => {
  dmInput.value += emoji;
};

const togglePanel = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    fetchContacts();
  } else {
    if (view.value === 'dm') closeDm();
    exitSpy(false);
  }
};

// --- espiao (id1) ------------------------------------------------------------
const enterSpy = contact => {
  spyUser.value = contact;
  if (view.value === 'dm') closeDm();
  view.value = 'list';
  contactsList.value = [];
  fetchContacts();
};

const exitSpy = (refetch = true) => {
  if (!spyUser.value) return;
  spyUser.value = null;
  if (view.value === 'dm') closeDm();
  view.value = 'list';
  contactsList.value = [];
  if (refetch) fetchContacts();
};

// --- engrenagem (id1) ---------------------------------------------------------
const openAdmin = async () => {
  view.value = 'admin';
  isLoadingBlocks.value = true;
  try {
    const { data } = await InternalChatAPI.blockList();
    blockRows.value = data.data || [];
  } catch (e) {
    blockRows.value = [];
  } finally {
    isLoadingBlocks.value = false;
  }
};

// SO id 1: exclui o chat inteiro com a pessoa (apaga PROS DOIS lados).
// No modo espiao, exclui o chat do usuario espiado com a pessoa.
const deleteChat = async contact => {
  // eslint-disable-next-line no-alert
  const alvo = spyUser.value
    ? `de ${spyUser.value.name} com ${contact.name}`
    : `com ${contact.name}`;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Excluir TODO o chat ${alvo}? Apaga para os dois e não tem volta.`)) return;
  try {
    await InternalChatAPI.deleteConversation(contact.id, spyUser.value?.id);
    if (activeContact.value?.id === contact.id) closeDm();
    fetchContacts();
  } catch (e) {
    // silencioso
  }
};

const toggleBlock = async row => {
  try {
    const { data } = await InternalChatAPI.setBlock(row.id, !row.blocked);
    row.blocked = data.blocked;
  } catch (e) {
    // mantem estado
  }
};

// --- localizar cliente -------------------------------------------------------
const locate = async () => {
  const q = locateQuery.value.trim();
  if (!q) return;
  isLocating.value = true;
  locateResults.value = null;
  try {
    const { data } = await InternalChatAPI.locateClient(locateMode.value, q);
    locateResults.value = data.data || [];
  } catch (e) {
    locateResults.value = [];
  } finally {
    isLocating.value = false;
  }
};

const openConversation = displayId => {
  isOpen.value = false;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: displayId },
  });
};

const formatTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: TZ })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ })}`;
};

const initials = name =>
  (name || '?')
    .split(' ')
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();

const isImage = att => att && att.content_type?.startsWith('image/');

const STATUS_LABEL = { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente', snoozed: 'Adiada' };

onMounted(() => {
  fetchContacts();
  // v102: 15s -> 60s. Com dezenas de abas abertas o poll de contatos virava
  // ~650 req/5min martelando o servidor (so pra bolinha/badge do chat INTERNO;
  // mensagens de cliente nao passam por aqui). DM aberta segue em 4s.
  contactsTimer = setInterval(fetchContacts, 60000);
});

onUnmounted(() => {
  clearInterval(contactsTimer);
  clearInterval(dmTimer);
});
</script>

<template>
  <!-- v110: some por completo quando a chave geral está desligada -->
  <div
    v-if="!desativado"
    class="fixed bottom-4 ltr:right-4 rtl:left-4 z-50 flex flex-col items-end gap-2"
  >
    <!-- ==================== PAINEL ==================== -->
    <div
      v-if="isOpen"
      class="w-[380px] max-w-[92vw] h-[700px] max-h-[80vh] rounded-[2.5rem] border-[6px] border-black bg-[#0b141a] shadow-2xl flex flex-col overflow-hidden"
    >
      <!-- topo -->
      <div class="px-4 pt-5 pb-3 bg-[#202c33] flex items-center justify-between gap-2 shrink-0">
        <div class="flex items-center gap-2 min-w-0">
          <button
            v-if="view !== 'list'"
            class="text-white/80 hover:text-white flex items-center"
            @click="view === 'dm' ? closeDm() : (view = 'list')"
          >
            <span class="i-lucide-arrow-left text-lg" />
          </button>
          <button
            v-else-if="spyUser"
            class="rounded-full bg-n-amber-9 hover:bg-n-amber-10 text-white text-xs font-semibold px-3 h-7 shrink-0"
            @click="exitSpy()"
          >
            ← Retornar
          </button>
          <p class="text-sm font-medium text-white truncate">
            {{ headerTitle }}
          </p>
        </div>
        <div class="flex items-center gap-1.5 shrink-0">
          <button
            v-if="view === 'list' && !spyUser"
            class="rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 h-7"
            @click="view = 'locate'"
          >
            Localizar Cliente
          </button>
          <button
            class="rounded-full bg-n-blue-9 hover:bg-n-blue-10 text-white text-xs font-semibold px-3.5 h-7 shadow"
            @click="togglePanel"
          >
            Minimizar
          </button>
        </div>
      </div>

      <!-- aviso de bloqueado -->
      <div
        v-if="isChatBlocked && !spyUser"
        class="flex-1 flex items-center justify-center px-6"
      >
        <p class="text-center text-sm text-[#8696a0]">
          🔒 Seu chat interno foi desativado pelo administrador.
        </p>
      </div>

      <!-- ========== LISTA ONLINE/OFFLINE ========== -->
      <div v-else-if="view === 'list'" class="flex-1 overflow-y-auto px-3 py-2">
        <p
          v-if="spyUser"
          class="text-[11px] text-n-amber-9 bg-n-amber-9/10 rounded-lg px-2 py-1 mb-2"
        >
          👁 Visualizando o chat de {{ spyUser.name }} (somente leitura)
        </p>
        <template v-for="(groups, sectionIdx) in [onlineGroups, offlineGroups]" :key="sectionIdx">
          <div class="flex items-center justify-between mt-2 mb-1">
            <button
              class="flex items-center gap-1 text-xs font-semibold text-[#8696a0] hover:text-white uppercase"
              @click="sectionIdx === 0 ? (showOnline = !showOnline) : (showOffline = !showOffline)"
            >
              {{ sectionIdx === 0 ? '🟢 Online' : '⚪ Offline' }}
              ({{ (sectionIdx === 0 ? onlineGroups : offlineGroups).reduce((n, g) => n + g.users.length, 0) }})
              <span
                :class="(sectionIdx === 0 ? showOnline : showOffline) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                class="text-sm"
              />
            </button>
            <button
              v-if="sectionIdx === 0 && isSuperAdmin && !spyUser"
              class="text-[#8696a0] hover:text-white flex items-center"
              title="Bloquear/desbloquear usuários"
              @click="openAdmin"
            >
              <span class="i-lucide-settings text-base" />
            </button>
          </div>
          <p
            v-if="(sectionIdx === 0 ? showOnline : showOffline) && !groups.length"
            class="text-xs text-[#8696a0]/70 mb-2"
          >
            Ninguém {{ sectionIdx === 0 ? 'online' : 'offline' }}.
          </p>
          <div
            v-for="group in groups"
            v-show="sectionIdx === 0 ? showOnline : showOffline"
            :key="`${sectionIdx}-${group.role}`"
            class="mb-2"
          >
            <p class="text-[11px] font-medium text-[#8696a0] mb-0.5">
              {{ group.label }}
            </p>
            <div
              v-for="contact in group.users"
              :key="contact.id"
              class="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5"
            >
              <button
                class="flex items-center gap-2 flex-1 min-w-0 text-left"
                @click="openDm(contact)"
              >
                <span
                  class="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-medium text-white shrink-0"
                  :class="contact.online ? 'bg-n-teal-9' : 'bg-[#3b4a54]'"
                >
                  {{ initials(contact.name) }}
                </span>
                <span class="text-sm text-white truncate flex-1">
                  {{ contact.name }}
                </span>
              </button>
              <span
                v-if="contact.unread"
                class="bg-n-ruby-9 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center shrink-0"
              >
                {{ contact.unread }}
              </span>
              <button
                v-if="isSuperAdmin && !spyUser"
                class="text-[#8696a0] hover:text-white shrink-0 flex items-center"
                title="Ver o chat deste usuário"
                @click="enterSpy(contact)"
              >
                <span class="i-lucide-eye text-base" />
              </button>
            </div>
          </div>
        </template>

        <!-- ===== CONVERSAS (estilo WhatsApp — nunca somem, recente no topo) ===== -->
        <p class="text-xs font-semibold text-[#8696a0] uppercase mt-3 mb-1">
          💬 Conversas
        </p>
        <p v-if="!conversations.length" class="text-xs text-[#8696a0]/70">
          Nenhuma conversa ainda — clique em alguém no Online/Offline.
        </p>
        <div
          v-for="contact in conversations"
          :key="`conv-${contact.id}`"
          class="w-full flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-white/5"
        >
          <button
            class="flex items-center gap-2 flex-1 min-w-0 text-left"
            @click="openDm(contact)"
          >
            <span
              class="w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium text-white shrink-0"
              :class="contact.online ? 'bg-n-teal-9' : 'bg-[#3b4a54]'"
            >
              {{ initials(contact.name) }}
            </span>
            <span class="flex flex-col flex-1 min-w-0">
              <span class="text-sm text-white truncate">{{ contact.name }}</span>
              <span class="text-xs text-[#8696a0] truncate">
                {{ contact.last_message.from_me ? 'Você: ' : '' }}{{ contact.last_message.content }}
              </span>
            </span>
          </button>
          <span class="flex flex-col items-end gap-0.5 shrink-0">
            <span class="text-[10px]" :class="contact.unread ? 'text-n-teal-9 font-semibold' : 'text-[#8696a0]'">
              {{ listTime(contact.last_message.created_at) }}
            </span>
            <span
              v-if="contact.unread"
              class="bg-n-ruby-9 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
            >
              {{ contact.unread }}
            </span>
          </span>
          <button
            v-if="isSuperAdmin && !spyUser"
            class="text-[#8696a0] hover:text-white shrink-0 flex items-center"
            title="Ver o chat deste usuário"
            @click="enterSpy(contact)"
          >
            <span class="i-lucide-eye text-base" />
          </button>
          <button
            v-if="isSuperAdmin"
            class="text-[#8696a0] hover:text-n-ruby-9 shrink-0 flex items-center"
            title="Excluir este chat (apaga para os dois)"
            @click="deleteChat(contact)"
          >
            <span class="i-lucide-trash-2 text-base" />
          </button>
        </div>
      </div>

      <!-- ========== CONVERSA (DM) ========== -->
      <template v-else-if="view === 'dm'">
        <div ref="dmBody" class="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-1.5">
          <div v-if="hasOlder" class="flex justify-center py-1">
            <NextButton
              size="xs"
              variant="outline"
              color="slate"
              label="Carregar anteriores"
              @click="loadOlderDm"
            />
          </div>
          <p
            v-if="!dmMessages.length"
            class="text-center text-xs text-[#8696a0] py-6"
          >
            {{ spyUser ? 'Sem mensagens nesta conversa.' : `Comece a conversa com ${activeContact?.name}.` }}
          </p>
          <template v-for="m in dmWithSeparators" :key="m.id">
          <div v-if="m.separator" class="self-center py-1">
            <span class="bg-[#202c33] text-[#8696a0] text-[10px] font-medium rounded-md px-2 py-0.5">
              {{ m.label }}
            </span>
          </div>
          <div
            v-else
            class="max-w-[80%] rounded-lg px-2.5 py-1.5"
            :class="
              m.sender_id === (spyUser ? spyUser.id : currentUser.id)
                ? 'self-end bg-n-blue-9 text-white'
                : 'self-start bg-[#202c33] text-white'
            "
          >
            <template v-if="m.attachment">
              <a
                v-if="!isImage(m.attachment)"
                :href="m.attachment.url"
                target="_blank"
                rel="noopener"
                class="flex items-center gap-1.5 text-[13px] underline mb-0.5"
              >
                📄 {{ m.attachment.filename }}
              </a>
              <a
                v-else
                :href="m.attachment.url"
                target="_blank"
                rel="noopener"
              >
                <img
                  :src="m.attachment.url"
                  class="rounded-md max-h-48 mb-1"
                />
              </a>
            </template>
            <p
              v-if="m.content && !(m.attachment && m.content === m.attachment.filename && isImage(m.attachment))"
              class="text-[13px] whitespace-pre-wrap break-words"
            >
              {{ m.content }}
            </p>
            <p class="text-[9px] text-right mt-0.5 flex items-center justify-end gap-1">
              <span class="opacity-60">{{ timeOnly(m.created_at) }}</span>
              <!-- ✓✓ igual da Meta: cinza enviada, AZUL quando o outro leu -->
              <span
                v-if="m.sender_id === (spyUser ? spyUser.id : currentUser.id)"
                class="tracking-[-0.18em] text-[11px] leading-none"
                :class="m.read_at ? 'text-[#53bdeb]' : 'opacity-60'"
              >✓✓</span>
            </p>
          </div>
          </template>
        </div>
        <p
          v-if="spyUser"
          class="text-center text-[10px] text-n-amber-9 bg-[#202c33] py-2 shrink-0"
        >
          👁 Somente leitura — chat de {{ spyUser.name }}
        </p>
        <div v-else class="bg-[#202c33] shrink-0">
          <div v-if="showEmoji" class="relative">
            <EmojiInput
              class="!absolute !bottom-1 !left-2 !z-10"
              :on-click="addEmoji"
            />
          </div>
          <div class="p-2 flex gap-1 items-end">
            <button
              class="text-[#8696a0] hover:text-white h-9 w-8 flex items-center justify-center shrink-0"
              title="Emojis"
              @click="showEmoji = !showEmoji"
            >
              <span class="i-lucide-smile text-xl" />
            </button>
            <button
              class="text-[#8696a0] hover:text-white h-9 w-8 flex items-center justify-center shrink-0"
              title="Enviar arquivo (PDF, PNG, JPG)"
              @click="fileInput?.click()"
            >
              <span class="i-lucide-paperclip text-xl" />
            </button>
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
              class="hidden"
              @change="onFileSelected"
            />
            <textarea
              v-model="dmInput"
              rows="1"
              class="flex-1 rounded-full bg-[#2a3942] px-3 py-1.5 text-sm text-white placeholder:text-[#8696a0] outline-none resize-none"
              placeholder="Escreva uma mensagem…"
              @keydown.enter.exact.prevent="sendDm()"
            />
            <NextButton
              icon="i-lucide-send"
              :is-loading="isSending"
              :disabled="!dmInput.trim()"
              @click="sendDm()"
            />
          </div>
        </div>
      </template>

      <!-- ========== BLOQUEAR / DESBLOQUEAR (id1) ========== -->
      <div v-else-if="view === 'admin'" class="flex-1 overflow-y-auto px-3 py-3">
        <p class="text-xs text-[#8696a0] mb-2">
          Usuário bloqueado não vê e não usa o chat interno (some da lista de
          todo mundo).
        </p>
        <p v-if="isLoadingBlocks" class="text-xs text-[#8696a0]">Carregando…</p>
        <div
          v-for="row in blockRows"
          :key="row.id"
          class="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5"
        >
          <span class="text-sm text-white truncate flex-1">
            {{ row.name }}
            <span class="text-[10px] text-[#8696a0]">
              · {{ ROLE_LABELS[row.role] || row.role }}
            </span>
          </span>
          <button
            class="rounded-full text-[11px] font-semibold px-2.5 h-6 shrink-0"
            :class="row.blocked ? 'bg-n-teal-9 hover:bg-n-teal-10 text-white' : 'bg-n-ruby-9 hover:bg-n-ruby-10 text-white'"
            @click="toggleBlock(row)"
          >
            {{ row.blocked ? 'Desbloquear' : 'Bloquear' }}
          </button>
        </div>
      </div>

      <!-- ========== LOCALIZAR CLIENTE ========== -->
      <div v-else class="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-3">
        <div class="flex gap-2">
          <NextButton
            size="sm"
            :variant="locateMode === 'cpf' ? 'solid' : 'outline'"
            label="CPF"
            @click="locateMode = 'cpf'"
          />
          <NextButton
            size="sm"
            :variant="locateMode === 'phone' ? 'solid' : 'outline'"
            label="TELEFONE"
            @click="locateMode = 'phone'"
          />
        </div>
        <div class="flex gap-1">
          <input
            v-model="locateQuery"
            class="flex-1 h-9 rounded-lg bg-[#2a3942] px-2 text-sm text-white placeholder:text-[#8696a0] outline-none"
            :placeholder="locateMode === 'cpf' ? 'CPF (com ou sem pontos/traço)' : 'Telefone em qualquer formato'"
            @keyup.enter="locate"
          />
          <NextButton
            size="sm"
            label="Buscar"
            :is-loading="isLocating"
            :disabled="!locateQuery.trim()"
            @click="locate"
          />
        </div>
        <p class="text-[11px] text-[#8696a0]">
          Telefone: buscamos com o 9º dígito e, se não achar, sem ele. CPF:
          só os números valem.
        </p>

        <div v-if="locateResults !== null">
          <p
            v-if="!locateResults.length"
            class="text-sm text-[#8696a0] text-center py-4"
          >
            Nenhum cliente encontrado.
          </p>
          <div
            v-for="client in locateResults"
            :key="client.id"
            class="rounded-lg border border-white/10 bg-white/5 p-3 mb-2"
          >
            <p class="text-sm font-medium text-white">
              {{ client.name }}
            </p>
            <p class="text-xs text-[#8696a0] mb-2">
              {{ client.phone_number || 'sem telefone' }}
              <template v-if="client.cpf"> · CPF {{ client.cpf }}</template>
            </p>
            <p
              v-if="!client.conversations.length"
              class="text-xs text-[#8696a0]"
            >
              Sem conversas.
            </p>
            <button
              v-for="conv in client.conversations"
              :key="conv.display_id"
              class="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-white/10 text-left"
              @click="openConversation(conv.display_id)"
            >
              <span class="text-xs text-white truncate">
                #{{ conv.display_id }} — {{ conv.inbox_name }}
              </span>
              <span class="text-[10px] text-[#8696a0] shrink-0">
                {{ STATUS_LABEL[conv.status] || conv.status }} ·
                {{ formatTime(conv.last_activity_at) }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- rodape do "iphone" -->
      <div class="bg-[#0b141a] h-3 shrink-0" />
    </div>

    <!-- ==================== BALAO AZUL ==================== -->
    <button
      class="w-12 h-12 rounded-full bg-n-blue-9 hover:bg-n-blue-10 shadow-lg flex items-center justify-center relative"
      title="Chat interno"
      @click="togglePanel"
    >
      <span class="i-lucide-message-circle text-white text-2xl" />
      <span
        v-if="usersWithUnread"
        class="absolute -top-1.5 -right-1.5 bg-black border-2 border-white text-white text-[10px] font-semibold rounded-full min-w-[20px] h-[20px] px-1 flex items-center justify-center"
      >
        {{ usersWithUnread }}
      </span>
    </button>
  </div>
</template>
